/**
 * Practice Areas Settings — barrister settings
 * Route: /barrister/settings/areas
 *
 * Each action (add / remove / level change) calls the API immediately.
 * No save button — changes apply on the spot.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/shared/BackButton';
import { toast } from 'sonner';
import { Tag, Plus, Trash, CircleNotch } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { SearchableSelect, type SelectOption } from '@/components/ui/searchable-select';

// ─── Types ────────────────────────────────────────────────────

type ExpertiseLevel = 'general' | 'senior' | 'lead';

interface BarristerArea {
  id: string;        // practiceAreaId
  name: string;
  level: ExpertiseLevel;
  yearsExp?: number | null;
}

interface AllArea {
  id: string;
  name: string;
}

// ─── API helpers ──────────────────────────────────────────────

async function fetchMyAreas(): Promise<BarristerArea[]> {
  const res = await api.get('/me/practice-areas');
  return res.data.data.areas ?? [];
}

async function fetchAllAreas(): Promise<AllArea[]> {
  const res = await api.get('/practice-areas');
  return res.data.data.practiceAreas ?? [];
}

async function addArea(practiceAreaId: string, expertiseLevel: ExpertiseLevel): Promise<void> {
  await api.post('/me/practice-areas', { practiceAreaId, expertiseLevel });
}

async function removeArea(practiceAreaId: string): Promise<void> {
  await api.delete(`/me/practice-areas/${practiceAreaId}`);
}

// ─── Constants ────────────────────────────────────────────────

const LEVEL_OPTIONS: { value: ExpertiseLevel; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'senior',  label: 'Senior'  },
  { value: 'lead',    label: 'Lead'    },
];

const LEVEL_BADGE: Record<ExpertiseLevel, string> = {
  general: 'bg-muted text-muted-foreground',
  senior:  'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300',
  lead:    'bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300',
};

const inputCls =
  'w-full rounded-xl border border-border/70 bg-background px-3.5 py-2.5 text-sm text-foreground focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all';

// ══════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════

export default function PracticeAreasSettingsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: myAreas = [], isLoading: loadingMine } = useQuery({
    queryKey: ['me-practice-areas'],
    queryFn: fetchMyAreas,
    staleTime: 0,
  });

  const { data: allAreas = [], isLoading: loadingAll } = useQuery({
    queryKey: ['all-practice-areas'],
    queryFn: fetchAllAreas,
    staleTime: 60_000,
  });

  // Add form state
  const [addAreaId, setAddAreaId] = useState('');
  const [addLevel, setAddLevel] = useState<ExpertiseLevel>('general');
  const [adding, setAdding] = useState(false);

  // Removal state
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Available areas = all areas minus those already added
  const myAreaIds = new Set(myAreas.map(a => a.id));
  const availableOptions: SelectOption[] = allAreas
    .filter(a => !myAreaIds.has(a.id))
    .map(a => ({ value: a.id, label: a.name }));

  const handleAdd = async () => {
    if (!addAreaId || adding) return;
    setAdding(true);
    try {
      await addArea(addAreaId, addLevel);
      qc.invalidateQueries({ queryKey: ['me-practice-areas'] });
      setAddAreaId('');
      setAddLevel('general');
      toast.success('Practice area added');
    } catch {
      toast.error('Failed to add practice area');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (areaId: string, areaName: string) => {
    if (removingId) return;
    setRemovingId(areaId);
    try {
      await removeArea(areaId);
      qc.invalidateQueries({ queryKey: ['me-practice-areas'] });
      toast.success(`${areaName} removed`);
    } catch {
      toast.error('Failed to remove practice area');
    } finally {
      setRemovingId(null);
    }
  };

  const isLoading = loadingMine || loadingAll;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <CircleNotch size={22} className="animate-spin text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl pb-20">
      {/* Reached from Settings but routed as a page of its own, so it inherits no back
          control from the Settings shell. History, not a fixed destination: the same page is
          opened from more than one place. */}
      <BackButton onClick={() => navigate(-1)} />

      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Practice Areas</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          The areas of law you accept instructions in and your expertise level in each.
        </p>
      </div>

      {/* Current practice areas */}
      <div className="rounded-2xl border border-border/60 bg-card p-6 mb-6">
        <div className="mb-5 flex items-start gap-3 border-b border-border/50 pb-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:text-brand-400">
            <Tag size={14} weight="bold" />
          </div>
          <div>
            <div className="text-sm font-semibold text-foreground">Your Practice Areas</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Shown on your public profile and used during matter allocation
            </div>
          </div>
        </div>

        {myAreas.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border/50 py-8 text-center">
            <Tag size={20} className="mx-auto mb-2 text-muted-foreground/30" />
            <div className="text-xs text-muted-foreground/60">
              No practice areas added yet — add your first below
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            {myAreas.map(area => (
              <div
                key={area.id}
                className="flex items-center gap-3 rounded-xl border border-border/50 px-4 py-3"
              >
                {/* Name */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">{area.name}</div>
                </div>

                {/* Level badge/select */}
                <select
                  value={area.level}
                  onChange={async e => {
                    const newLevel = e.target.value as ExpertiseLevel;
                    // optimistic update then re-fetch — the API handles PATCH via re-add
                    try {
                      await removeArea(area.id);
                      await addArea(area.id, newLevel);
                      qc.invalidateQueries({ queryKey: ['me-practice-areas'] });
                      toast.success('Expertise level updated');
                    } catch {
                      toast.error('Failed to update level');
                      qc.invalidateQueries({ queryKey: ['me-practice-areas'] });
                    }
                  }}
                  className={`rounded-lg border border-border/60 bg-background px-2.5 py-1.5 text-xs font-semibold transition focus:outline-none focus:ring-1 focus:ring-brand-400 ${LEVEL_BADGE[area.level]}`}
                >
                  {LEVEL_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>

                {/* Remove */}
                <button
                  onClick={() => handleRemove(area.id, area.name)}
                  disabled={removingId === area.id}
                  className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs text-muted-foreground transition hover:border-red-200 dark:border-red-500/30 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                >
                  {removingId === area.id ? (
                    <CircleNotch size={11} className="animate-spin" />
                  ) : (
                    <><Trash size={11} />Remove</>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add new practice area */}
      <div className="rounded-2xl border border-border/60 bg-card p-6">
        <div className="mb-5 flex items-start gap-3 border-b border-border/50 pb-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:text-brand-400">
            <Plus size={14} weight="bold" />
          </div>
          <div>
            <div className="text-sm font-semibold text-foreground">Add Practice Area</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Select an area and your expertise level
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Area select */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Practice Area
            </label>
            {availableOptions.length === 0 ? (
              <div className="rounded-xl border border-border/50 bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
                All available practice areas have been added.
              </div>
            ) : (
              <SearchableSelect
                value={addAreaId}
                onChange={setAddAreaId}
                options={availableOptions}
                placeholder="Search practice areas…"
              />
            )}
          </div>

          {/* Expertise level */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Expertise Level
            </label>
            <select
              value={addLevel}
              onChange={e => setAddLevel(e.target.value as ExpertiseLevel)}
              className={inputCls}
            >
              {LEVEL_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Add button */}
          <button
            onClick={handleAdd}
            disabled={!addAreaId || adding || availableOptions.length === 0}
            className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-xs font-semibold transition-all ${
              addAreaId && !adding
                ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            {adding ? (
              <><CircleNotch size={13} className="animate-spin" />Adding…</>
            ) : (
              <><Plus size={13} weight="bold" />Add practice area</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
